/**
 * Pure-JS Monero key generation for Cashero.
 *
 * Generates a Monero wallet (seed, spend key, view key, address) entirely
 * in the browser without needing the monero-ts WASM module. The WASM module
 * is only required later for syncing and sending transactions.
 *
 * Uses:
 *   - @noble/hashes for Keccak-256 (original, with 0x01 padding — Monero-compatible)
 *   - @noble/curves for Ed25519 (scalar reduction mod l, base point multiplication)
 *   - Web Crypto API for random bytes
 */

import { keccak_256 } from '@noble/hashes/sha3';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';
import { ENGLISH_WORDLIST } from './wordlist-en';

// Ed25519 group order (prime subgroup order)
const ED25519_GROUP_ORDER =
  0x1000000000000000000000000000000014def9dea2f79cd65812631a5cf5d3edn;

// Mainnet network byte for standard addresses
const MAINNET_NETWORK_BYTE = 0x12;

// Base58 alphabet
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/**
 * Reduce a 256-bit value modulo the Ed25519 group order `l`.
 * This is Monero's `sc_reduce32` operation.
 */
function scReduce32(bytes: Uint8Array): bigint {
  let value = 0n;
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8n) | BigInt(bytes[i]);
  }
  return value % ED25519_GROUP_ORDER;
}

/**
 * Perform Keccak-256 hash-to-scalar: hash the input and reduce mod l.
 * This is Monero's `Hs()` function.
 */
function hashToScalar(data: Uint8Array): bigint {
  const hash = keccak_256(data);
  return scReduce32(hash);
}

/**
 * Multiply the Ed25519 base point by a scalar.
 * Uses the Ed25519 curve from @noble/curves.
 */
import { ed25519 } from '@noble/curves/ed25519';

function scalarBaseMult(scalar: bigint): Uint8Array {
  // Convert scalar to bytes (little-endian, 32 bytes)
  const scalarBytes = new Uint8Array(32);
  let s = scalar;
  for (let i = 0; i < 32; i++) {
    scalarBytes[i] = Number(s & 0xffn);
    s >>= 8n;
  }
  // Use Ed25519 point multiplication via the public API
  return ed25519.getPublicKey(scalarBytes);
}

// ----------------------------------------------------------------
// Ed25519 point operations for subaddress derivation
// ----------------------------------------------------------------

/** Ed25519 field prime: 2^255 - 19 */
const P = BigInt('0x7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffed');

/** Ed25519 curve parameter d = -121665 / 121666 mod p */
const D = (BigInt(-121665) * modinv(BigInt(121666), P) + P) % P;

/** Modular inverse via Fermat's little theorem: a^(p-2) mod p */
function modinv(a: bigint, p: bigint): bigint {
  let exp = p - 2n;
  let result = 1n;
  let base = a % p;
  while (exp > 0n) {
    if (exp & 1n) result = (result * base) % p;
    base = (base * base) % p;
    exp >>= 1n;
  }
  return result;
}

/** Decompress an Ed25519 point from its compressed 32-byte format (y + sign bit). */
function ed25519Decompress(compressed: Uint8Array): [bigint, bigint] {
  const last = compressed[31];
  const sign = last >> 7;
  let y = BigInt(last & 0x7f);
  for (let i = 30; i >= 0; i--) {
    y = (y << 8n) | BigInt(compressed[i]);
  }

  const yy = (y * y) % P;
  const u = (yy - 1n + P) % P;
  const v = (D * yy + 1n) % P;
  const v_inv = modinv(v, P);
  let x = modsqrt((u * v_inv) % P);

  if (x === null) throw new Error('Failed to decompress Ed25519 point');

  if ((x & 1n) !== BigInt(sign)) {
    x = P - x;
  }

  return [x, y];
}

/** Modular square root for p ≡ 5 (mod 8) — the Ed25519 case. */
function modsqrt(a: bigint): bigint | null {
  a = ((a % P) + P) % P;
  if (a === 0n) return 0n;

  const sqrt = powMod(a, (P + 3n) / 8n);
  if ((sqrt * sqrt) % P === a) return sqrt;

  const sqrt2 = (sqrt * powMod(2n, (P - 1n) / 4n)) % P;
  if ((sqrt2 * sqrt2) % P === a) return sqrt2;

  return null;
}

/** Modular exponentiation: a^e mod p */
function powMod(a: bigint, e: bigint): bigint {
  let result = 1n;
  let base = a % P;
  let exp = e;
  while (exp > 0n) {
    if (exp & 1n) result = (result * base) % P;
    base = (base * base) % P;
    exp >>= 1n;
  }
  return result;
}

/** Add two Ed25519 points in affine coordinates. */
function ed25519Add(P: [bigint, bigint], Q: [bigint, bigint]): [bigint, bigint] {
  const [x1, y1] = P;
  const [x2, y2] = Q;

  const x1y2 = (x1 * y2) % P;
  const x2y1 = (x2 * y1) % P;
  const y1y2 = (y1 * y2) % P;
  const x1x2 = (x1 * x2) % P;

  const denomX = (1n + D * x1x2 * y1y2) % P;
  const denomY = (1n - D * x1x2 * y1y2 + P) % P;

  const x3 = ((x1y2 + x2y1) % P) * modinv(denomX, P) % P;
  const y3 = ((y1y2 + x1x2) % P) * modinv(denomY, P) % P;

  return [x3, y3];
}

/** Multiply an Ed25519 point by a scalar using double-and-add. */
function ed25519ScalarMult(scalar: bigint, point: [bigint, bigint]): [bigint, bigint] {
  // Identity point (0, 1) on twisted Edwards curves
  let result: [bigint, bigint] = [0n, 1n];
  let addend = point;
  let s = scalar;

  while (s > 0n) {
    if (s & 1n) result = ed25519Add(result, addend);
    addend = ed25519Add(addend, addend);
    s >>= 1n;
  }

  return result;
}

/** Compress an Ed25519 point to 32 bytes (y-coordinate with sign bit). */
function ed25519Compress(point: [bigint, bigint]): Uint8Array {
  const [x, y] = point;
  const sign = x & 1n;
  const bytes = new Uint8Array(32);

  // Write y as little-endian 32 bytes
  let yVal = y;
  for (let i = 0; i < 31; i++) {
    bytes[i] = Number(yVal & 0xffn);
    yVal >>= 8n;
  }
  // Last byte: high 7 bits are y's high bits, low 1 bit is sign of x
  bytes[31] = Number(yVal & 0x7fn) | (sign > 0n ? 0x80 : 0x00);

  return bytes;
}

/**
 * Generate a Monero subaddress for a given wallet seed + index.
 *
 * @param spendKeyHex - Private spend key (hex string, 64 chars)
 * @param viewKeyHex - Private view key (hex string, 64 chars)
 * @param accountIndex - Account index (usually 0)
 * @param subaddressIndex - Subaddress index (0 = primary, 1+ = subaddress)
 * @returns The subaddress string (base58)
 */
export function generateSubaddress(
  spendKeyHex: string,
  viewKeyHex: string,
  accountIndex: number,
  subaddressIndex: number,
): string {
  const aBytes = hexToBytes(spendKeyHex);
  const aBig = BigInt('0x' + spendKeyHex);
  const vBig = BigInt('0x' + viewKeyHex);

  // Step 1: m = Hs("SubAddr\0" || a_priv || account_index_le32 || subaddress_index_le32)
  const prefix = new TextEncoder().encode('SubAddr\0');
  const indexBytes = new Uint8Array(8);
  const dv = new DataView(indexBytes.buffer);
  dv.setUint32(0, accountIndex, true);  // little-endian
  dv.setUint32(4, subaddressIndex, true); // little-endian

  const preimage = new Uint8Array([...prefix, ...aBytes, ...indexBytes]);
  const m = hashToScalar(preimage);

  // Step 2: D = m * G (base point)
  const Gx = BigInt(ed25519.CURVE.Gx);
  const Gy = BigInt(ed25519.CURVE.Gy);
  const D = ed25519ScalarMult(m, [Gx, Gy]);

  // Step 3: B_priv_scalar * G = public spend key point
  const publicSpendKeyCompressed = ed25519.getPublicKey(aBytes);
  const B = ed25519Decompress(publicSpendKeyCompressed);

  // Step 4: C = D + B (subaddress public spend key)
  const C = ed25519Add(D, B);

  // Step 5: Subaddress view key: v * C
  const V = ed25519ScalarMult(vBig, C);

  // Step 6: Build address
  const Cbytes = ed25519Compress(C);
  const Vbytes = ed25519Compress(V);

  const addressRaw = new Uint8Array(1 + 32 + 32);
  addressRaw[0] = MAINNET_NETWORK_BYTE;
  addressRaw.set(Cbytes.slice(0, 32), 1);
  addressRaw.set(Vbytes.slice(0, 32), 33);

  const checksum = keccakChecksum(addressRaw);
  const addressWithChecksum = new Uint8Array(69);
  addressWithChecksum.set(addressRaw);
  addressWithChecksum.set(checksum, 65);

  return moneroBase58Encode(addressWithChecksum);
}

/**
 * Monero-style CRC-32 checksum using Keccak-256.
 * Monero uses the first 4 bytes of Keccak-256 as the address checksum.
 */
function keccakChecksum(data: Uint8Array): Uint8Array {
  const hash = keccak_256(data);
  return hash.slice(0, 4);
}

/**
 * Monero base58 encoding (block-based variant).
 * Each 8-byte block encodes to 11 base58 characters.
 */
function moneroBase58Encode(data: Uint8Array): string {
  if (data.length === 0) return '';

  // Convert to big integer
  let value = 0n;
  for (const b of data) {
    value = (value << 8n) | BigInt(b);
  }

  // Encode to base58
  const result: string[] = [];
  while (value > 0n) {
    result.push(BASE58_ALPHABET[Number(value % 58n)]);
    value /= 58n;
  }

  // Handle leading zero bytes
  // Monero base58 preserves leading zeros as '1' chars
  // But the block-based encoding is more complex
  // For simplicity, use the standard bigint-based approach

  // Pad with leading '1's for each leading zero byte
  let leadingZeros = 0;
  while (leadingZeros < data.length && data[leadingZeros] === 0) {
    leadingZeros++;
  }
  for (let i = 0; i < leadingZeros; i++) {
    result.push('1');
  }

  return result.reverse().join('');
}

/**
 * Generate a complete set of Monero wallet keys from a random 32-byte seed.
 *
 * Returns seed phrase (25 words), address, view key, and spend key.
 */
export function generateMoneroKeys(): {
  seed: string;
  address: string;
  viewKey: string;
  spendKey: string;
} {
  // 1. Generate random 32-byte private spend key
  const spendKeyBytes = crypto.getRandomValues(new Uint8Array(32));

  // 2. Reduce the spend key scalar mod l
  const spendScalar = scReduce32(spendKeyBytes);

  // 3. Compute private view key: Hs(spend_key_bytes)
  const viewScalar = hashToScalar(spendKeyBytes);

  // 4. Compute public spend key: a × G
  const publicSpendKey = scalarBaseMult(spendScalar);
  // Ed25519 returns 32-byte x-only public key — Monero uses compressed format

  // 5. Compute public view key: v × G
  // Use the scalar directly (already mod l from hashToScalar)
  const publicViewKey = scalarBaseMult(viewScalar);

  // 6. Build the address
  // Format: network_byte (0x12) + public_spend_key (32) + public_view_key (32)
  const addressRaw = new Uint8Array(1 + 32 + 32);
  addressRaw[0] = MAINNET_NETWORK_BYTE;
  // Ed25519 ExtendedPoint.toRawBytes() returns 32 bytes (x-coordinate only)
  // Monero uses the full 32-byte public key
  addressRaw.set(publicSpendKey.slice(0, 32), 1);
  addressRaw.set(publicViewKey.slice(0, 32), 33);

  // Append 4-byte checksum (Keccak-256 of address data)
  const checksum = keccakChecksum(addressRaw);
  const addressWithChecksum = new Uint8Array(69);
  addressWithChecksum.set(addressRaw);
  addressWithChecksum.set(checksum, 65);

  const address = moneroBase58Encode(addressWithChecksum);

  // 7. Generate mnemonic seed phrase (25 words)
  const seed = generateMnemonic(spendKeyBytes);

  // Convert keys to hex strings
  const spendKeyHex = bytesToHex(spendKeyBytes);
  const viewKeyHex = bytesToHex(
    (() => {
      const vBytes = new Uint8Array(32);
      let s = viewScalar;
      for (let i = 0; i < 32; i++) {
        vBytes[i] = Number(s & 0xffn);
        s >>= 8n;
      }
      return vBytes;
    })(),
  );

  return {
    seed,
    address,
    viewKey: viewKeyHex,
    spendKey: spendKeyHex,
  };
}

/**
 * Generate a Monero 25-word mnemonic seed phrase from a 32-byte seed.
 *
 * Encoding:
 * 1. Split 32 bytes into 8 groups of 4 bytes (32 bits each)
 * 2. Each 32-bit group → 3 words using the 1626-word list:
 *    w1 = value % 1626
 *    w2 = (value / 1626 + w1) % 1626
 *    w3 = (value / 1626² + w2) % 1626
 * 3. 8 groups × 3 words = 24 words
 * 4. CRC-32 of the 24 word indices → 25th word
 */
function generateMnemonic(seed: Uint8Array): string {
  const NWORDS = 1626;
  const wordIndices: number[] = [];

  // Split into 8 groups of 4 bytes
  for (let group = 0; group < 8; group++) {
    // Read 4 bytes as a 32-bit unsigned integer (big-endian)
    const offset = group * 4;
    const value =
      (seed[offset] << 24) |
      (seed[offset + 1] << 16) |
      (seed[offset + 2] << 8) |
      seed[offset + 3];

    // Encode as 3 words
    const w1 = value % NWORDS;
    const w2 = (Math.floor(value / NWORDS) + w1) % NWORDS;
    const w3 = (Math.floor(value / (NWORDS * NWORDS)) + w2) % NWORDS;

    wordIndices.push(w1, w2, w3);
  }

  // CRC-32 of the word indices (first 24 words)
  const checksumIndex = crc32Indices(wordIndices.slice(0, 24)) % 25;
  wordIndices.push(checksumIndex);

  // Map indices to words
  return wordIndices.map((i) => ENGLISH_WORDLIST[i]).join(' ');
}

/**
 * Simple CRC-32 for an array of integers (word indices).
 * Uses the standard CRC-32 polynomial (0xEDB88320).
 */
function crc32Indices(indices: number[]): number {
  // Build CRC table
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let crc = i;
    for (let j = 0; j < 8; j++) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[i] = crc;
  }

  let crc = 0xffffffff;
  for (const idx of indices) {
    crc = table[(crc ^ idx) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
